<template>
  <div class="merge-customers-container merge-customers-main merge-customers-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Merge Customers']" />

    <form class="merge-customers-form" @submit.prevent="handleMerge">
      <div class="glass-tab-view glass-tab-view--flush merge-customers-glass-shell">
        <div class="glass-tab-view__content merge-customers-page-stack">
          <div class="merge-customers-header-block">
            <h2 class="merge-customers-title">Merge Customers</h2>
            <p class="merge-customers-subtitle">
              Combine duplicate customer records by moving activity from one customer into another
            </p>
          </div>

          <div class="merge-customers-comparison">
            <div class="merge-customers-section merge-customers-section--from glass-tab-panel--nested">
              <h3 class="merge-customers-section-title">Customer From</h3>
              <p class="merge-customers-section-hint">Source record to merge away</p>
              <div class="merge-customers-form-row merge-customers-form-row--stack">
                <div class="merge-customers-form-group merge-customers-form-group--dropdown">
                  <LabelDropdown
                    label="Customer"
                    select-id="merge-customers-from"
                    v-model="selectedFrom"
                    flex
                    wide
                    required
                    searchable
                    search-placeholder="Search customers..."
                    :options="customerSelectOptions"
                  />
                </div>
                <div class="merge-customers-form-group">
                  <LabelInputField
                    input-id="merge-customers-from-id"
                    label="Customer ID"
                    :model-value="fromCustomer?.id || ''"
                    readonly
                    flex
                  />
                </div>
                <div class="merge-customers-form-group merge-customers-form-group--checkbox">
                  <LabelCheckboxField
                    input-id="merge-customers-from-active"
                    label="Active"
                    :model-value="!!fromCustomer?.active"
                    disabled
                    flex
                  />
                </div>
                <div
                  class="merge-customers-form-group merge-customers-form-group--balance"
                  :class="{ 'merge-customers-form-group--balance-empty': !fromCustomer?.balance }"
                >
                  <LabelInputField
                    input-id="merge-customers-from-balance"
                    label="Balance"
                    :model-value="fromCustomer?.balance || ''"
                    readonly
                    flex
                  />
                </div>
              </div>
            </div>

            <div class="merge-customers-comparison-arrow" aria-hidden="true">
              <i class="bi bi-arrow-right"></i>
            </div>

            <div class="merge-customers-section merge-customers-section--to glass-tab-panel--nested">
              <h3 class="merge-customers-section-title">Customer To</h3>
              <p class="merge-customers-section-hint">Target record to keep</p>
              <div class="merge-customers-form-row merge-customers-form-row--stack">
                <div class="merge-customers-form-group merge-customers-form-group--dropdown">
                  <LabelDropdown
                    label="Customer"
                    select-id="merge-customers-to"
                    v-model="selectedTo"
                    flex
                    wide
                    required
                    searchable
                    search-placeholder="Search customers..."
                    :options="customerSelectOptions"
                  />
                </div>
                <div class="merge-customers-form-group">
                  <LabelInputField
                    input-id="merge-customers-to-id"
                    label="Customer ID"
                    :model-value="toCustomer?.id || ''"
                    readonly
                    flex
                  />
                </div>
                <div class="merge-customers-form-group merge-customers-form-group--checkbox">
                  <LabelCheckboxField
                    input-id="merge-customers-to-active"
                    label="Active"
                    :model-value="!!toCustomer?.active"
                    disabled
                    flex
                  />
                </div>
                <div
                  class="merge-customers-form-group merge-customers-form-group--balance"
                  :class="{ 'merge-customers-form-group--balance-empty': !toCustomer?.balance }"
                >
                  <LabelInputField
                    input-id="merge-customers-to-balance"
                    label="Balance"
                    :model-value="toCustomer?.balance || ''"
                    readonly
                    flex
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="merge-customers-form-actions glass-tab-view__actions">
            <SecondaryButton label="Cancel" @click="handleCancel" />
            <PrimaryButton
              label="Merge"
              icon="bi bi-arrow-left-right"
              @click="handleMerge"
            />
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import '../styles/MergeCustomers.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'MergeCustomers',
  components: {
    BrucdcumHeader,
    LabelDropdown,
    LabelInputField,
    LabelCheckboxField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      customers: [
        { id: 'C-001', name: 'Acme Corp', active: true, balance: '$1,200.00' },
        { id: 'C-002', name: 'Beta LLC', active: false, balance: '$350.00' },
        { id: 'C-003', name: 'Gamma Inc', active: true, balance: '$2,500.00' },
        { id: 'C-004', name: 'Delta Ltd', active: true, balance: '$0.00' },
      ],
      selectedFrom: '',
      selectedTo: '',
    };
  },
  computed: {
    customerSelectOptions() {
      return this.customers.map((customer) => ({
        value: customer.id,
        label: customer.name,
      }));
    },
    fromCustomer() {
      return this.customers.find((c) => c.id === this.selectedFrom);
    },
    toCustomer() {
      return this.customers.find((c) => c.id === this.selectedTo);
    },
  },
  methods: {
    handleMerge() {
      if (!this.selectedFrom || !this.selectedTo) {
        this.$toast && this.$toast.error('Please select both Customer From and Customer To.');
        return;
      }
      if (this.selectedFrom === this.selectedTo) {
        this.$toast && this.$toast.error('Customer From and Customer To must be different.');
        return;
      }
      this.$toast && this.$toast.info('Merge customers coming soon!');
    },
    handleCancel() {
      if (this.$router) {
        this.$router.go(-1);
        return;
      }
      this.selectedFrom = '';
      this.selectedTo = '';
    },
  },
};
</script>
